import './util/markAppBundleLoaded';
import './util/handleError';
import './util/setupServiceWorker';
import './global/init';

import { onFullyIdle } from './lib/teact/teact';
import TeactDOM from './lib/teact/teact-dom';
import {
  getActions, getGlobal,
} from './global';

import {
  CALL_LIVEKIT_ENABLED, DEBUG, STRICTERDOM_ENABLED,
} from './config';
import { enableStrict, requestMutation } from './lib/fasterdom/fasterdom';
import { loadLiveKitClient } from './lib/vibecalls/liveKitScript';
import { selectChat, selectCurrentMessageList, selectPeerFullInfo, selectTabState } from './global/selectors';
import { selectSharedSettings } from './global/selectors/sharedState';
import { betterView } from './util/betterView';
import setupSafariRecompositeOnRestore from './util/browser/forceSafariRecomposite';
import { IS_TAURI } from './util/browser/globalEnvironment';
import listenOtherClients from './util/browser/listenOtherClients';
import { requestGlobal, subscribeToMultitabBroadcastChannel } from './util/browser/multitab';
import { requestPersistentStorage } from './util/cacheApi';
import { establishMultitabRole, subscribeToMasterChange } from './util/establishMultitabRole';
import setupEventLoopWatchdog from './util/eventLoopWatchdog';
import { initGlobal } from './util/init';
import { initLocalization } from './util/localization';
import { setupMediaFrameCache } from './util/mediaFirstFrameCache';
import { hydrateLoadedStaticProxyUrls, subscribeToMediaCacheTrim } from './util/mediaProxyUrl';
import { MULTITAB_STORAGE_KEY } from './util/multiaccount';
import { runQrApproveFlow } from './util/qrApproveLogin';
import { onBeforeUnload } from './util/schedulers';
import initTauriApi from './util/tauri/initTauriApi';
import setupTauriListeners from './util/tauri/setupTauriListeners';
import updateWebmanifest from './util/updateWebmanifest';

import App from './components/App';

import './assets/fonts/roboto.css';
import './styles/index.scss';

if (STRICTERDOM_ENABLED) {
  enableStrict();
}

if (IS_TAURI) {
  initTauriApi();
  setupTauriListeners();
}

init();

async function init() {
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> INIT');
  }

  if (!(window as any).isCompatTestPassed) return;

  setupSafariRecompositeOnRestore();
  setupEventLoopWatchdog();
  setupMediaFrameCache();
  // Fire-and-forget, like the frame cache above: it only needs to settle before the message list
  // paints, which is several `await`s away (global init, chat load)
  void hydrateLoadedStaticProxyUrls();
  subscribeToMediaCacheTrim();
  listenOtherClients();

  subscribeToMultitabBroadcastChannel();
  await requestGlobal(APP_VERSION);
  localStorage.setItem(MULTITAB_STORAGE_KEY, '1');
  onBeforeUnload(() => {
    const global = getGlobal();
    if (Object.keys(global.byTabId).length === 1) {
      localStorage.removeItem(MULTITAB_STORAGE_KEY);
    }
  });

  await initGlobal();
  getActions().init();

  getActions().updateShouldEnableDebugLog();
  getActions().updateShouldDebugExportedSenders();

  // Preload the LiveKit client for the call paths (1:1 peer-to-peer + group), gated on the call
  // env, so the media layer is ready when a call starts. (The legacy Call Center SDK is no longer
  // used — 1:1 signaling is peer-to-peer over the backend WS and group calls join LiveKit directly.)
  // Held until the app is idle: the bundle is 366 KB to parse and compile, it was the single
  // largest thing loaded at boot, and no session that never places a call needs it then. The call
  // paths await `loadLiveKitClient` themselves before touching the client, so a call that starts
  // before this fires still works — it just pays the load it would have paid anyway.
  // Read the flag from `config` rather than through `phoneCallLiveKit`'s one-line getter: importing
  // the getter dragged that module's whole static graph (livekitRoom + roomToken, 21 KB of source)
  // into the ENTRY chunk, so every session parsed the call machinery at boot to answer a boolean the
  // preload below then usually skips. Same value, same gate.
  if (CALL_LIVEKIT_ENABLED) {
    onFullyIdle(() => {
      void loadLiveKitClient();
    });
  }

  // If this device opened a scanned QR-login URL, run the approve flow (no-op unless logged in
  // here and a `qrApprove` token is present).
  void runQrApproveFlow();

  const global = getGlobal();

  // Only for a signed-in user, and only once per outcome: this is what keeps the browser from evicting
  // the whole origin (session included) under disk pressure, but on Firefox it surfaces a permission
  // prompt — which a first-time visitor staring at a login screen should not be shown.
  if (global.auth.state === 'authorizationStateReady') {
    void requestPersistentStorage();
  }

  initLocalization(selectSharedSettings(global).language, true);

  subscribeToMasterChange((isMasterTab) => {
    getActions()
      .switchMultitabRole({ isMasterTab }, { forceSyncOnIOs: true });
  });
  const shouldReestablishMasterToSelf = getGlobal().auth.state !== 'authorizationStateReady';
  establishMultitabRole(shouldReestablishMasterToSelf);

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> START INITIAL RENDER');
  }

  requestMutation(() => {
    updateWebmanifest();

    TeactDOM.render(
      <App />,
      document.getElementById('root')!,
    );

    betterView();
  });

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> FINISH INITIAL RENDER');
  }

  if (DEBUG) {
    document.addEventListener('dblclick', () => {
      const currentGlobal = getGlobal();
      const currentMessageList = selectCurrentMessageList(currentGlobal);
      // eslint-disable-next-line no-console
      console.warn('TAB STATE', selectTabState(currentGlobal));
      // eslint-disable-next-line no-console
      console.warn('GLOBAL STATE', currentGlobal);
      if (currentMessageList) {
        // eslint-disable-next-line no-console
        console.warn(
          'CURRENT MESSAGE LIST',
          selectChat(currentGlobal, currentMessageList.chatId),
          selectPeerFullInfo(currentGlobal, currentMessageList.chatId),
          currentGlobal.messages.byChatId[currentMessageList.chatId],
        );
      }
    });
  }
}

onBeforeUnload(() => {
  const actions = getActions();
  actions.leaveGroupCall?.({ isPageUnload: true });
  actions.hangUp?.({ isPageUnload: true });
});
